import React, { useState, useEffect, useRef, useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { useCalculatorStore } from '../../store/calculatorStore';
import { Select } from '../ui/Select';
import { DatePicker } from '../ui/DatePicker';
import { ARMDetailsSection } from './ARMDetailsSection';
import { VariableRateSection } from './VariableRateSection';
import { CanadianTermSection } from './CanadianTermSection';
import { GDSTDSCalculator } from './GDSTDSCalculator';
import type {
  Country,
  LoanProgram,
  MortgageType,
  ARMDetails,
  VariableDetails,
  CanadianTermDetails,
  MortgageTermsData,
  PaymentFrequency,
} from '../../types/mortgageTypes';
import {
  applyMortgageTypeToggle,
  getMortgageTypeSummaryLabel,
  getMortgageTypeToggleValue,
  migrateCountrySwitch,
  normalizeMortgageTerms,
} from '../../utils/canadianMortgageType';
import {
  createFHADetails,
  createVADetails,
  createUSDADetails,
  createCMHCDetails,
  validateDownPayment,
} from '../../utils/mortgageCalculations';

interface MortgageTermsSectionProps {
  tabId: string;
}

/** Large hero input card with label + monospaced value */
const HeroInput: React.FC<{
  label: string;
  prefix?: string;
  suffix?: string;
  value: number;
  step?: number;
  min?: number;
  decimals?: number;
  onChange: (v: number) => void;
  hint?: React.ReactNode;
}> = ({ label, prefix, suffix, value, step = 1, min = 0, decimals = 0, onChange, hint }) => {
  const inputId = useId();
  const [raw, setRaw] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setRaw(String(value));
  }, [value, focused]);

  const displayValue = focused
    ? raw
    : value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  return (
    <div
      className={`flex-1 min-w-[160px] bg-cream rounded-xl border-2 transition-colors px-4 pt-3 pb-4 ${
        focused ? 'border-gold-500' : 'border-stone-border hover:border-stone-dark'
      }`}
    >
      <label
        htmlFor={inputId}
        className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-2"
      >
        {label}
      </label>
      <div className="flex items-baseline gap-1">
        {prefix && <span className="text-xl text-ink-muted font-light">{prefix}</span>}
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          step={step}
          value={displayValue}
          onFocus={() => {
            setFocused(true);
            setRaw(String(value));
          }}
          onBlur={() => {
            setFocused(false);
            const parsed = parseFloat(raw.replace(/,/g, ''));
            if (!isNaN(parsed) && parsed >= min) onChange(parsed);
            else setRaw(String(value));
          }}
          onChange={e => setRaw(e.target.value.replace(/[^0-9.]/g, ''))}
          className="w-full bg-transparent font-mono text-3xl text-ink font-medium outline-none border-none p-0"
        />
        {suffix && <span className="text-base text-ink-muted ml-1">{suffix}</span>}
      </div>
      {hint && <div className="mt-1.5">{hint}</div>}
    </div>
  );
};

/** Down Payment card with dual-editable dollar + percent inputs */
const DownPaymentCard: React.FC<{
  downPaymentAmount: number;
  downPaymentPercentage: number;
  loanAmount: number;
  onAmountChange: (v: number) => void;
  onPercentChange: (v: number) => void;
}> = ({ downPaymentAmount, downPaymentPercentage, loanAmount, onAmountChange, onPercentChange }) => {
  const amountInputId = useId();
  const percentInputId = useId();
  const [amountRaw, setAmountRaw] = useState(String(downPaymentAmount));
  const [amountFocused, setAmountFocused] = useState(false);
  const [percentRaw, setPercentRaw] = useState(downPaymentPercentage.toFixed(2));
  const [percentFocused, setPercentFocused] = useState(false);

  useEffect(() => {
    if (!amountFocused) setAmountRaw(String(downPaymentAmount));
  }, [downPaymentAmount, amountFocused]);

  useEffect(() => {
    if (!percentFocused) setPercentRaw(downPaymentPercentage.toFixed(2));
  }, [downPaymentPercentage, percentFocused]);

  const displayAmount = amountFocused
    ? amountRaw
    : downPaymentAmount.toLocaleString('en-US', { maximumFractionDigits: 0 });

  return (
    <div
      className={`flex-1 min-w-[160px] bg-cream rounded-xl border-2 transition-colors px-4 pt-3 pb-4 ${
        amountFocused || percentFocused
          ? 'border-gold-500'
          : 'border-stone-border hover:border-stone-dark'
      }`}
    >
      <span
        id={`${amountInputId}-legend`}
        className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-2"
      >
        Down Payment
      </span>
      <div className="flex items-baseline gap-1">
        <span className="text-xl text-ink-muted font-light">$</span>
        <input
          id={amountInputId}
          type="text"
          inputMode="numeric"
          aria-labelledby={`${amountInputId}-legend`}
          value={displayAmount}
          onFocus={() => { setAmountFocused(true); setAmountRaw(String(downPaymentAmount)); }}
          onBlur={() => {
            setAmountFocused(false);
            const parsed = parseFloat(amountRaw.replace(/,/g, ''));
            if (!isNaN(parsed) && parsed >= 0) onAmountChange(parsed);
            else setAmountRaw(String(downPaymentAmount));
          }}
          onChange={e => setAmountRaw(e.target.value.replace(/[^0-9.]/g, ''))}
          className="w-full bg-transparent font-mono text-3xl text-ink font-medium outline-none border-none p-0"
        />
      </div>
      <div className="mt-1.5 flex items-center gap-3">
        <div className="flex items-baseline gap-0.5">
          <label htmlFor={percentInputId} className="sr-only">
            Down payment as percent of home price
          </label>
          <input
            id={percentInputId}
            type="text"
            inputMode="decimal"
            value={percentFocused ? percentRaw : downPaymentPercentage.toFixed(2)}
            onFocus={() => { setPercentFocused(true); setPercentRaw(downPaymentPercentage.toFixed(2)); }}
            onBlur={() => {
              setPercentFocused(false);
              const parsed = parseFloat(percentRaw);
              if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) onPercentChange(parsed);
              else setPercentRaw(downPaymentPercentage.toFixed(2));
            }}
            onChange={e => setPercentRaw(e.target.value.replace(/[^0-9.]/g, ''))}
            className="bg-transparent font-mono text-base text-ink font-bold outline-none border-none p-0 w-[5ch]"
          />
          <span className="text-base text-ink font-bold">%</span>
        </div>
        <span className="text-xs text-live font-medium">
          ↳ Loan: ${loanAmount.toLocaleString('en-US')}
        </span>
      </div>
    </div>
  );
};

/** Fixed / ARM toggle button pair */
const MortgageTypeToggle: React.FC<{
  value: MortgageType;
  onChange: (v: MortgageType) => void;
}> = ({ value, onChange }) => {
  const options: { val: MortgageType; label: string }[] = [
    { val: 'fixed', label: 'Fixed' },
    { val: 'adjustable', label: 'ARM' },
    { val: 'variable', label: 'Variable' },
  ];
  return (
    <div className="flex rounded-lg bg-stone border border-stone-border overflow-hidden">
      {options.map(opt => (
        <button
          key={opt.val}
          onClick={() => onChange(opt.val)}
          className={`flex-1 py-2 px-3 text-sm font-medium transition-colors ${
            value === opt.val
              ? 'bg-navy-800 text-white'
              : 'text-ink-muted hover:text-ink'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

function getTermsSummary(terms: MortgageTermsData): string {
  const typeLabel = getMortgageTypeSummaryLabel(terms);
  const getFreqLabel = (f: PaymentFrequency): string => {
    if (f === 'bi-weekly') return 'Bi-Weekly';
    if (f === 'accelerated-bi-weekly') return 'Accel. Bi-Weekly';
    return 'Monthly';
  };
  const parts = [
    typeLabel,
    `${terms.amortizationPeriod}yr`,
    getFreqLabel(terms.paymentFrequency),
  ];
  return parts.join(' · ');
}

export const MortgageTermsSection: React.FC<MortgageTermsSectionProps> = ({ tabId }) => {
  const calculatorTabs = useCalculatorStore(s => s.calculatorTabs);
  const updateCalculatorTabData = useCalculatorStore(s => s.updateCalculatorTabData);
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [isPulsing, setIsPulsing] = useState(false);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showValidationError, setShowValidationError] = useState<string | null>(null);

  const toggleAdvanced = () => {
    const next = !showAdvanced;
    setShowAdvanced(next);
    if (!next) {
      setIsPulsing(true);
      pulseTimerRef.current = setTimeout(() => setIsPulsing(false), 6000);
    } else {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      setIsPulsing(false);
    }
  };

  const tab = calculatorTabs.find(t => t.id === tabId);
  const currentMortgageTerms = tab?.data.mortgageTerms;

  useEffect(() => {
    const mortgageTerms = currentMortgageTerms;
    if (!mortgageTerms) return;
    if (
      !mortgageTerms.loanProgram ||
      !Number.isFinite(mortgageTerms.homePrice) ||
      mortgageTerms.homePrice <= 0 ||
      !Number.isFinite(mortgageTerms.downPaymentAmount) ||
      mortgageTerms.downPaymentAmount < 0
    )
      return;
    const principal = mortgageTerms.homePrice - mortgageTerms.downPaymentAmount;
    let needsUpdate = false;
    const updatedTerms = { ...mortgageTerms };
    if (mortgageTerms.loanProgram === 'fha' && !mortgageTerms.fhaDetails) {
      updatedTerms.fhaDetails = createFHADetails(principal, mortgageTerms.homePrice, mortgageTerms.amortizationPeriod);
      needsUpdate = true;
    } else if (mortgageTerms.loanProgram === 'va' && !mortgageTerms.vaDetails) {
      updatedTerms.vaDetails = createVADetails(mortgageTerms.downPaymentPercentage, true, false);
      needsUpdate = true;
    } else if (mortgageTerms.loanProgram === 'usda' && !mortgageTerms.usdaDetails) {
      updatedTerms.usdaDetails = createUSDADetails();
      needsUpdate = true;
    } else if (mortgageTerms.loanProgram === 'cmhc' && !mortgageTerms.cmhcDetails) {
      updatedTerms.cmhcDetails = createCMHCDetails(mortgageTerms.downPaymentPercentage);
      needsUpdate = true;
    }
    if (needsUpdate) {
      updateCalculatorTabData(tabId, { mortgageTerms: updatedTerms });
    }
  }, [currentMortgageTerms, tabId, updateCalculatorTabData]);

  if (!tab) return <div className="p-4 text-center text-ink-muted">Calculator not found</div>;

  const { mortgageTerms, annualExpenses, amortizationSchedule } = tab.data;

  const monthlyPropertyTax =
    annualExpenses.propertyTaxes.type === 'amount'
      ? annualExpenses.propertyTaxes.value
      : (mortgageTerms.homePrice * (annualExpenses.propertyTaxes.value / 100)) / 12;

  const firstPayment = amortizationSchedule[0]?.payment ?? 0;
  const loanAmount = mortgageTerms.homePrice - mortgageTerms.downPaymentAmount;

  const getLoanProgramOptions = (country: Country) =>
    country === 'USA'
      ? [
          { value: 'conventional', label: 'Conventional' },
          { value: 'fha', label: 'FHA Loan' },
          { value: 'va', label: 'VA Loan' },
          { value: 'usda', label: 'USDA Loan' },
          { value: 'jumbo', label: 'Jumbo Loan' },
        ]
      : [
          { value: 'conventional', label: 'Conventional (20%+ down)' },
          { value: 'cmhc', label: 'CMHC Insured (<20% down)' },
        ];

  const handleChange = (field: keyof MortgageTermsData, value: MortgageTermsData[keyof MortgageTermsData]) => {
    const updatedTerms = { ...mortgageTerms, [field]: value };

    if (field === 'country') {
      updatedTerms.loanProgram = 'conventional';
      updatedTerms.fhaDetails = undefined;
      updatedTerms.vaDetails = undefined;
      updatedTerms.usdaDetails = undefined;
      updatedTerms.cmhcDetails = undefined;
      Object.assign(
        updatedTerms,
        migrateCountrySwitch(updatedTerms, value as Country)
      );
    }

    if (field === 'mortgageType') {
      Object.assign(
        updatedTerms,
        applyMortgageTypeToggle(updatedTerms, value as MortgageType)
      );
    }

    if (field === 'loanProgram') {
      updatedTerms.fhaDetails = undefined;
      updatedTerms.vaDetails = undefined;
      updatedTerms.usdaDetails = undefined;
      updatedTerms.cmhcDetails = undefined;
      if ((value === 'va' || value === 'usda') && mortgageTerms.downPaymentPercentage > 0) {
        updatedTerms.downPaymentAmount = 0;
        updatedTerms.downPaymentPercentage = 0;
      }
    }

    if (field === 'downPaymentAmount' && mortgageTerms.homePrice) {
      updatedTerms.downPaymentPercentage = Number(((value / mortgageTerms.homePrice) * 100).toFixed(2));
    } else if (field === 'downPaymentPercentage' && mortgageTerms.homePrice) {
      updatedTerms.downPaymentAmount = Number((mortgageTerms.homePrice * (value / 100)).toFixed(2));
    } else if (field === 'homePrice' && mortgageTerms.downPaymentPercentage) {
      updatedTerms.downPaymentAmount = Number((value * (mortgageTerms.downPaymentPercentage / 100)).toFixed(2));
    }

    // Auto-default PMI to 0.8% when USA conventional drops below 20% down,
    // and clear it when the condition no longer applies.
    const pmiApplies =
      updatedTerms.country === 'USA' &&
      updatedTerms.loanProgram === 'conventional' &&
      updatedTerms.downPaymentPercentage < 20;
    const wasZero = updatedTerms.pmi.value === 0;
    if (pmiApplies && wasZero) {
      updatedTerms.pmi = { ...updatedTerms.pmi, value: 0.8 };
    } else if (!pmiApplies && updatedTerms.pmi.value > 0) {
      updatedTerms.pmi = { ...updatedTerms.pmi, value: 0 };
    }

    if (field === 'rate' && updatedTerms.mortgageType === 'variable' && updatedTerms.variableDetails) {
      updatedTerms.variableDetails = {
        ...updatedTerms.variableDetails,
        discount: Number((updatedTerms.variableDetails.primeRate - value).toFixed(2)),
      };
    }

    if (
      (field === 'downPaymentPercentage' || field === 'loanProgram' || field === 'homePrice') &&
      updatedTerms.loanProgram && updatedTerms.homePrice && updatedTerms.country
    ) {
      const validation = validateDownPayment(
        updatedTerms.loanProgram as LoanProgram,
        updatedTerms.downPaymentPercentage,
        updatedTerms.homePrice,
        updatedTerms.country
      );
      if (!validation.valid && validation.message) {
        setShowValidationError(validation.message);
        setTimeout(() => setShowValidationError(null), 5000);
      } else {
        setShowValidationError(null);
      }
    }

    updateCalculatorTabData(tabId, {
      mortgageTerms: normalizeMortgageTerms(updatedTerms),
    });
  };

  const handleCanadianTermDetailsChange = (canadianTermDetails: CanadianTermDetails) => {
    updateCalculatorTabData(tabId, { mortgageTerms: { ...mortgageTerms, canadianTermDetails } });
  };

  const handleARMDetailsChange = (armDetails: ARMDetails) => {
    updateCalculatorTabData(tabId, { mortgageTerms: { ...mortgageTerms, armDetails } });
  };

  const handleVariableDetailsChange = (variableDetails: VariableDetails) => {
    const currentRate = variableDetails.primeRate - variableDetails.discount;
    updateCalculatorTabData(tabId, {
      mortgageTerms: normalizeMortgageTerms({
        ...mortgageTerms,
        variableDetails,
        rate: Number(currentRate.toFixed(2)),
      }),
    });
  };

  return (
    <div
      className="px-5 pb-5 space-y-4"
      onClick={e => { if (e.target === e.currentTarget) toggleAdvanced(); }}
    >
      {showValidationError && (
        <div className="bg-gold-50 border border-gold-300 text-ink px-4 py-3 rounded-xl text-sm">
          {showValidationError}
        </div>
      )}

      {/* ── Hero Input Row ── */}
      <div className="flex flex-wrap gap-3">
        {/* Home Price */}
        <HeroInput
          label="Home Price"
          prefix="$"
          value={mortgageTerms.homePrice}
          onChange={v => handleChange('homePrice', v)}
        />

        {/* Down Payment */}
        <DownPaymentCard
          downPaymentAmount={mortgageTerms.downPaymentAmount}
          downPaymentPercentage={mortgageTerms.downPaymentPercentage}
          loanAmount={loanAmount}
          onAmountChange={v => handleChange('downPaymentAmount', v)}
          onPercentChange={v => handleChange('downPaymentPercentage', v)}
        />

        {/* Interest Rate */}
        <HeroInput
          label="Interest Rate"
          suffix="%"
          value={mortgageTerms.rate}
          step={0.05}
          min={0}
          decimals={2}
          onChange={v => handleChange('rate', v)}
          hint={
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-ink-faint">Low</span>
              <input
                type="range"
                min={0.5}
                max={15}
                step={0.05}
                value={mortgageTerms.rate}
                onChange={e => handleChange('rate', Number(e.target.value))}
                className="flex-1 mx-2 h-1 accent-gold-500"
              />
              <span className="text-[10px] text-ink-faint">High</span>
            </div>
          }
        />
      </div>

      {/* ── Advanced Toggle ── */}
      <div className="flex items-center justify-between gap-3">
        <div className="group relative">
          {!showAdvanced && (
            <div className="pointer-events-none absolute bottom-full right-0 mb-2 w-56 rounded-lg bg-navy-900 px-3 py-2 text-xs text-navy-100 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10">
              <p className="font-semibold text-white mb-1">Hidden settings</p>
              <p className="text-navy-300">Loan type · Amortization · Payment frequency · Start date · Country</p>
              <div className="absolute bottom-0 right-4 translate-y-full border-4 border-transparent border-t-navy-900" />
            </div>
          )}
          <button
            onClick={toggleAdvanced}
            className="flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink transition-colors"
          >
            {isPulsing && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-500" />
              </span>
            )}
            <span className="text-gold-600">{showAdvanced ? 'Fewer terms' : 'More terms'}</span>
            <ChevronDown
              size={14}
              className={`text-gold-600 transition-transform ${showAdvanced ? 'rotate-180' : ''} ${isPulsing ? 'animate-bounce' : ''}`}
            />
          </button>
        </div>
        {!showAdvanced && (
          <p className="text-xs text-ink-muted font-normal text-right shrink-0 min-w-0 max-w-[55%] sm:max-w-none truncate" title={getTermsSummary(mortgageTerms)}>
            {getTermsSummary(mortgageTerms)}
          </p>
        )}
      </div>

      {/* ── Advanced Fields ── */}
      {showAdvanced && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 border-t border-stone-border">
          {/* Mortgage Type toggle */}
          <div>
            <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
              Mortgage Type
            </label>
            <MortgageTypeToggle
              value={getMortgageTypeToggleValue(mortgageTerms)}
              onChange={v => handleChange('mortgageType', v)}
            />
          </div>

          {/* Loan Program */}
          <div>
            <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
              Loan Program
            </label>
            <Select
              label=""
              value={mortgageTerms.loanProgram}
              onChange={v => handleChange('loanProgram', v as LoanProgram)}
              options={getLoanProgramOptions(mortgageTerms.country)}
            />
          </div>

          {/* Amortization */}
          <div>
            <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
              Amortization
            </label>
            <Select
              label=""
              value={String(mortgageTerms.amortizationPeriod)}
              onChange={v => handleChange('amortizationPeriod', Number(v))}
              options={[
                { value: '10', label: '10 years' },
                { value: '15', label: '15 years' },
                { value: '20', label: '20 years' },
                { value: '25', label: '25 years' },
                { value: '30', label: '30 years' },
              ]}
            />
          </div>

          {/* Payment Frequency */}
          <div>
            <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
              Payment Frequency
            </label>
            <Select
              label=""
              value={mortgageTerms.paymentFrequency}
              onChange={v => handleChange('paymentFrequency', v)}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'semi-monthly', label: 'Semi-monthly (1st & 15th)' },
                { value: 'bi-weekly', label: 'Bi-weekly (Standard)' },
                { value: 'accelerated-bi-weekly', label: 'Bi-weekly (Accelerated) 💰' },
                { value: 'weekly', label: 'Weekly (Standard)' },
                { value: 'accelerated-weekly', label: 'Weekly (Accelerated) 💰' },
              ]}
            />
            {(mortgageTerms.paymentFrequency === 'accelerated-bi-weekly' ||
              mortgageTerms.paymentFrequency === 'accelerated-weekly') && (
              <p className="text-xs text-live mt-1">Pays off faster & saves interest</p>
            )}
            {mortgageTerms.paymentFrequency === 'accelerated-bi-weekly' && mortgageTerms.country === 'USA' && (
              <p className="text-xs text-amber-600 mt-1">
                US lenders don't typically offer accelerated bi-weekly as a built-in product. To replicate this in practice, make your normal bi-weekly payments and add one extra monthly payment per year toward principal.
              </p>
            )}
            {mortgageTerms.paymentFrequency === 'accelerated-weekly' && mortgageTerms.country === 'USA' && (
              <p className="text-xs text-amber-600 mt-1">
                US lenders don't typically offer accelerated weekly as a built-in product. To replicate this in practice, make your normal weekly payments and add one extra monthly payment per year toward principal.
              </p>
            )}
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
              Start Date
            </label>
            <DatePicker
              label=""
              selectedDate={mortgageTerms.startDate}
              onChange={date => handleChange('startDate', date)}
              showMonthYearPicker
              placeholderText="Select month and year"
            />
          </div>

          {/* Country */}
          <div>
            <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
              Country / Region
            </label>
            <Select
              label=""
              value={mortgageTerms.country}
              onChange={v => handleChange('country', v as Country)}
              options={[
                { value: 'USA', label: '🇺🇸 United States' },
                { value: 'Canada', label: '🇨🇦 Canada' },
              ]}
            />
          </div>

        </div>
      )}

      {/* ── Program Info Panels + ARM / Variable / Canadian sections ── */}
      {showAdvanced && (
        <>
          {mortgageTerms.loanProgram === 'fha' && mortgageTerms.fhaDetails && (
            <div className="p-4 bg-navy-50 border border-navy-100 rounded-xl text-sm text-ink">
              <p className="font-semibold text-navy-800 mb-1">FHA Loan Details</p>
              <ul className="space-y-0.5 text-ink-muted text-xs">
                <li>• Upfront MIP: ${mortgageTerms.fhaDetails.upfrontMIP.toLocaleString()} (1.75%)</li>
                <li>• Annual MIP: {(mortgageTerms.fhaDetails.annualMIPRate * 100).toFixed(2)}%</li>
                <li>• Duration: {mortgageTerms.fhaDetails.mipDurationYears > 0 ? `${mortgageTerms.fhaDetails.mipDurationYears} yrs` : 'Lifetime'}</li>
              </ul>
            </div>
          )}
          {mortgageTerms.loanProgram === 'va' && mortgageTerms.vaDetails && (
            <div className="p-4 bg-live-light border border-live/20 rounded-xl text-sm">
              <p className="font-semibold text-live mb-1">VA Loan Details</p>
              <ul className="space-y-0.5 text-ink-muted text-xs">
                <li>• Funding Fee: {(mortgageTerms.vaDetails.fundingFeeRate * 100).toFixed(2)}%</li>
                <li>• No monthly mortgage insurance</li>
                <li>• Zero down payment available</li>
              </ul>
            </div>
          )}
          {mortgageTerms.loanProgram === 'usda' && mortgageTerms.usdaDetails && (
            <div className="p-4 bg-gold-50 border border-gold-200 rounded-xl text-sm">
              <p className="font-semibold text-gold-700 mb-1">USDA Loan Details</p>
              <ul className="space-y-0.5 text-ink-muted text-xs">
                <li>• Upfront Fee: {(mortgageTerms.usdaDetails.upfrontFee * 100).toFixed(2)}%</li>
                <li>• Annual Fee: {(mortgageTerms.usdaDetails.annualFee * 100).toFixed(2)}%</li>
                <li>• Rural area property required</li>
              </ul>
            </div>
          )}
          {mortgageTerms.loanProgram === 'cmhc' && mortgageTerms.cmhcDetails && (
            <div className="p-4 bg-navy-50 border border-navy-200 rounded-xl text-sm">
              <p className="font-semibold text-navy-700 mb-1">CMHC Insurance</p>
              <ul className="space-y-0.5 text-ink-muted text-xs">
                <li>• Premium: {(mortgageTerms.cmhcDetails.premiumRate * 100).toFixed(2)}% of loan</li>
                <li>• Added to mortgage principal</li>
              </ul>
            </div>
          )}
          {mortgageTerms.country === 'USA' &&
            mortgageTerms.mortgageType === 'adjustable' &&
            mortgageTerms.armDetails && (
            <ARMDetailsSection
              armDetails={mortgageTerms.armDetails}
              onChange={handleARMDetailsChange}
              principal={mortgageTerms.homePrice - mortgageTerms.downPaymentAmount}
              amortizationMonths={mortgageTerms.amortizationPeriod * 12}
              initialRate={mortgageTerms.rate}
              country={mortgageTerms.country}
            />
          )}
          {mortgageTerms.mortgageType === 'variable' && mortgageTerms.variableDetails && (
            <VariableRateSection
              variableDetails={mortgageTerms.variableDetails}
              onChange={handleVariableDetailsChange}
              country={mortgageTerms.country}
              principal={mortgageTerms.homePrice - mortgageTerms.downPaymentAmount}
              amortizationMonths={mortgageTerms.amortizationPeriod * 12}
              firstMonthlyPayment={firstPayment}
            />
          )}
          {mortgageTerms.country === 'Canada' && (
            <GDSTDSCalculator tabId={tabId} mortgagePayment={firstPayment} monthlyPropertyTax={monthlyPropertyTax} />
          )}
          {mortgageTerms.country === 'Canada' && mortgageTerms.canadianTermDetails && (
            <CanadianTermSection
              termDetails={mortgageTerms.canadianTermDetails}
              amortizationYears={mortgageTerms.amortizationPeriod}
              onChange={handleCanadianTermDetailsChange}
              principal={mortgageTerms.homePrice - mortgageTerms.downPaymentAmount}
              mortgageType={mortgageTerms.mortgageType}
              currentRate={mortgageTerms.rate}
            />
          )}
        </>
      )}
    </div>
  );
};
